"use client";

import { getSMTPSettings } from "./settings";

export interface EmailData {
  to: string;
  subject: string;
  html?: string;
  text?: string;
  attachments?: Array<{
    filename: string;
    content?: string;
    path?: string;
    contentType?: string;
  }>;
}

/**
 * Send email via API route
 * Automatically includes SMTP settings from localStorage
 */
export async function sendEmail(data: EmailData): Promise<{ success: boolean; messageId?: string; error?: string }> {
  try {
    // Get SMTP settings from localStorage
    const smtpSettings = getSMTPSettings();
    
    const response = await fetch("/api/send-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...data,
        smtp: smtpSettings.enabled ? smtpSettings : undefined,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || result.details || "Failed to send email");
    }

    return { success: true, messageId: result.messageId };
  } catch (error: any) {
    console.error("Email sending error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Send invoice via email
 */
export async function sendInvoiceEmail(
  to: string,
  invoice: {
    id: string;
    client: string;
    event: string;
    total: number;
    final: number;
    balance: number;
    dueDate: string;
    pdfBase64?: string;
  }
): Promise<{ success: boolean; messageId?: string; error?: string }> {
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #C9A84C;">Invoice from Shalimar Banquet Hall</h2>
      <p>Dear ${invoice.client},</p>
      <p>Please find attached your invoice details:</p>
      
      <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Invoice #</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${invoice.id}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Event</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${invoice.event}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Total Amount</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">₹${invoice.final.toLocaleString()}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Balance Due</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd; color: #DC2626;">₹${invoice.balance.toLocaleString()}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Due Date</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${invoice.dueDate}</td>
        </tr>
      </table>
      
      <p>If you have any questions, please don't hesitate to contact us.</p>
      
      <p>Best regards,<br>
      <strong>Shalimar Banquet Hall</strong><br>
      📞 +91 98765 43210<br>
      📧 info@shalimar.in</p>
    </div>
  `;

  const attachments = invoice.pdfBase64 
    ? [{
        filename: `Invoice-${invoice.id}.pdf`,
        content: invoice.pdfBase64,
        contentType: "application/pdf",
      }]
    : [];

  return sendEmail({
    to,
    subject: `Invoice ${invoice.id} - Shalimar Banquet Hall`,
    html,
    attachments,
  });
}

/**
 * Send quotation via email
 */
export async function sendQuotationEmail(
  to: string,
  quotation: {
    id: string;
    client: string;
    eventType: string;
    eventDate: string;
    guests: number;
    total: number;
    validUntil: string;
    pdfBase64?: string;
  }
): Promise<{ success: boolean; messageId?: string; error?: string }> {
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #C9A84C;">Quotation from Shalimar Banquet Hall</h2>
      <p>Dear ${quotation.client},</p>
      <p>Thank you for considering Shalimar Banquet Hall for your special event. Please find your quotation details below:</p>
      
      <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Quotation #</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${quotation.id}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Event Type</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${quotation.eventType}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Event Date</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${quotation.eventDate}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Guests</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${quotation.guests}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd; background: #FEF3C7;"><strong>Total Amount</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd; background: #FEF3C7; font-size: 18px; font-weight: bold;">₹${quotation.total.toLocaleString()}</td>
        </tr>
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;"><strong>Valid Until</strong></td>
          <td style="padding: 10px; border: 1px solid #ddd;">${quotation.validUntil}</td>
        </tr>
      </table>
      
      <p style="color: #666; font-size: 14px;">This quotation is valid until ${quotation.validUntil}. To confirm your booking, please contact us at your earliest convenience.</p>
      
      <p>If you have any questions, please don't hesitate to contact us.</p>
      
      <p>Best regards,<br>
      <strong>Shalimar Banquet Hall</strong><br>
      📞 +91 98765 43210<br>
      📧 info@shalimar.in</p>
    </div>
  `;

  const attachments = quotation.pdfBase64 
    ? [{
        filename: `Quotation-${quotation.id}.pdf`,
        content: quotation.pdfBase64,
        contentType: "application/pdf",
      }]
    : [];

  return sendEmail({
    to,
    subject: `Quotation ${quotation.id} - Shalimar Banquet Hall`,
    html,
    attachments,
  });
}
